import React, { useState, useEffect } from 'react';
import { FileText, AlertCircle, Calendar, Loader, ChevronRight } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

const Models = () => {
  const { fetchGet } = useApi();
  const [error, setError] = useState('');
  const [addOneHour, setAddOneHour] = useState(true);

  // State for prediction viewer
  const [viewerDate, setViewerDate] = useState('');
  // Use default model values
  const viewerFixIModel = 'v2.0.0';
  const viewerFixIIModel = 'v1.0.0-fix2';
  const viewerFixISpread = 5;
  const viewerPredsDiffCutoff = 5;
  const [viewerLoading, setViewerLoading] = useState(false);
  const [fixIPredictions, setFixIPredictions] = useState(null);
  const [fixIIPredictions, setFixIIPredictions] = useState(null);

  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    setViewerDate(tomorrowStr);
  }, []);

  // Fetch predictions for viewer
  const fetchPredictionsForViewer = async () => {
    if (!viewerDate) {
      alert('Please select a date');
      return;
    }

    setViewerLoading(true);
    setError('');
    setFixIPredictions(null);
    setFixIIPredictions(null);

    try {
      // Fetch Fix I predictions
      const fixIResponse = await fetchGet(
        `/models/${viewerDate}?model_version=${encodeURIComponent(viewerFixIModel)}`
      );
      const fixIData = await fixIResponse.json();

      // Fetch Fix II predictions
      const fixIIResponse = await fetchGet(
        `/models/${viewerDate}?model_version=${encodeURIComponent(viewerFixIIModel)}`
      );
      const fixIIData = await fixIIResponse.json();

      if (fixIData.status === 'success') {
        setFixIPredictions(fixIData);
      } else {
        setError(`Fix I: ${fixIData.message || 'Failed to load predictions'}`);
      }

      if (fixIIData.status === 'success') {
        setFixIIPredictions(fixIIData);
      } else {
        setError(prev => prev ? `${prev}, Fix II: ${fixIIData.message}` : `Fix II: ${fixIIData.message || 'Failed to load predictions'}`);
      }
    } catch (err) {
      setError('Network error. Failed to fetch predictions.');
      console.error('Error fetching predictions:', err);
    } finally {
      setViewerLoading(false);
    }
  };

  const calculateDailyAverage = (csvContent) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return null;
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length <= 1) return null;

    const headers = lines[0].split(',');
    const predictionIndex = headers.indexOf('prediction');

    if (predictionIndex === -1) return null;

    const rows = lines.slice(1).map(line => line.split(','));

    let predictionSum = 0;
    let count = 0;

    rows.forEach(row => {
      const predValue = parseFloat(row[predictionIndex]);
      if (!isNaN(predValue)) {
        predictionSum += predValue;
        count++;
      }
    });

    if (count === 0) return null;

    return parseFloat((predictionSum / count).toFixed(2));
  };

  const formatCSVAsTable = (csvContent, modelType) => {
    if (!csvContent || csvContent === 'Predictions not available yet.') {
      return (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Predictions not available for {modelType}</p>
        </div>
      );
    }

    const lines = csvContent.trim().split('\n');
    if (lines.length === 0) return null;

    const headers = lines[0].split(',');
    const rows = lines.slice(1).map(line => line.split(','));

    // Define which columns should be displayed
    const displayColumns = ['date', 'hour', 'prediction'];
    const displayHeaders = headers.filter(header => displayColumns.includes(header));
    const displayIndices = displayHeaders.map(header => headers.indexOf(header));

    // Format header names for display
    const formatHeaderName = (header) => {
      const headerMap = {
        'prediction': 'Prediction',
        'date': 'Date',
        'hour': 'Hour'
      };
      return headerMap[header] || header;
    };

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {displayHeaders.map((header, index) => (
                <th
                  key={index}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {formatHeaderName(header)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50`}>
                {displayIndices.map((colIndex, displayIndex) => {
                  const header = displayHeaders[displayIndex];
                  const cell = row[colIndex];

                  // Apply hour adjustment if it's the hour column and addOneHour is true
                  if (header === 'hour' && addOneHour && !isNaN(cell)) {
                    const hourValue = parseInt(cell) + 1;
                    return (
                      <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                        {hourValue}
                      </td>
                    );
                  }

                  // Format numeric values (except date and hour)
                  if (header !== 'date' && header !== 'hour' && !isNaN(cell) && cell !== '') {
                    return (
                      <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                        {parseFloat(cell).toFixed(2)}
                      </td>
                    );
                  }

                  return (
                    <td key={displayIndex} className="px-3 py-1.5 whitespace-nowrap text-sm text-gray-900">
                      {cell}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Model Predictions Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Model Predictions</h2>
        <p className="text-gray-600 mt-1">View predictions</p>
      </div>

      {/* Prediction Viewer Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">View Predictions</h3>

        <div className="mb-6">
          {/* Date Input */}
          <div className="max-w-xs">
            <label className="flex items-center space-x-2 text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} />
              <span>Date</span>
            </label>
            <input
              type="date"
              value={viewerDate}
              onChange={(e) => setViewerDate(e.target.value)}
              min="2025-12-01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Show Predictions Button - Full Width */}
        <button
          onClick={fetchPredictionsForViewer}
          disabled={viewerLoading || !viewerDate}
          className={`w-full flex items-center justify-center space-x-2 px-6 py-2.5 rounded-md font-medium transition-colors ${
            viewerLoading || !viewerDate
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {viewerLoading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <ChevronRight className="w-4 h-4" />
              <span>Show Predictions</span>
            </>
          )}
        </button>

        {/* Hour display format toggle */}
        <div className="mt-4 flex items-center space-x-3">
          <span className="text-sm text-gray-700">Display hours as 1-24</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={addOneHour}
              onChange={(e) => setAddOneHour(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 border border-gray-400 shadow-sm peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:border-blue-500 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border after:border-gray-400 after:shadow-sm after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error loading predictions</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Predictions Display */}
        {fixIPredictions && fixIPredictions.content && (() => {
          const fixIAvg = calculateDailyAverage(fixIPredictions.content);
          const fixIIAvg = fixIIPredictions && fixIIPredictions.content
            ? calculateDailyAverage(fixIIPredictions.content)
            : null;

          // Calculate prices and warnings
          let buyPrice = null;
          let sellPrice = null;
          let predsDiff = null;
          let skipBuy = false;
          let skipSell = false;

          if (fixIAvg !== null) {
            buyPrice = (fixIAvg - viewerFixISpread).toFixed(2);
            sellPrice = (fixIAvg + viewerFixISpread).toFixed(2);

            if (fixIIAvg !== null && fixIIAvg !== 0) {
              predsDiff = ((fixIAvg - fixIIAvg) / fixIIAvg) * 100;
              // Skip buy if Fix I > Fix II by more than cutoff
              if (predsDiff > viewerPredsDiffCutoff) {
                skipBuy = true;
              }
              // Skip sell if Fix II > Fix I by more than cutoff (negative diff)
              if (predsDiff < -viewerPredsDiffCutoff) {
                skipSell = true;
              }
            }
          }

          return (
            <div className="mt-6">
              {/* Buy/Sell Prices */}
              {fixIAvg !== null && (
                <div className="flex space-x-4 mb-6">
                  {skipBuy ? (
                    <div className="flex-1 bg-red-50 rounded-lg px-4 py-3 border border-red-300">
                      <p className="text-sm text-red-600 font-medium">Buy Skipped</p>
                      <p className="text-xs text-red-500">Preds diff: {predsDiff.toFixed(2)}%</p>
                    </div>
                  ) : (
                    <div className="flex-1 bg-green-50 rounded-lg px-4 py-3 border border-green-200">
                      <p className="text-sm text-gray-600">Buy Price</p>
                      <p className="text-xl font-bold text-green-600">{buyPrice}</p>
                    </div>
                  )}
                  {skipSell ? (
                    <div className="flex-1 bg-red-50 rounded-lg px-4 py-3 border border-red-300">
                      <p className="text-sm text-red-600 font-medium">Sell Skipped</p>
                      <p className="text-xs text-red-500">Preds diff: {predsDiff.toFixed(2)}%</p>
                    </div>
                  ) : (
                    <div className="flex-1 bg-red-50 rounded-lg px-4 py-3 border border-red-200">
                      <p className="text-sm text-gray-600">Sell Price</p>
                      <p className="text-xl font-bold text-red-600">{sellPrice}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Fix I Predictions Table */}
              <div className="border border-gray-200 rounded-lg">
                <div className="bg-blue-50 px-4 py-3 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-800">Predictions</h4>
                </div>
                <div className="p-4">
                  {formatCSVAsTable(fixIPredictions.content, 'Fix I')}
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Models;
